// Iter 90f — Marketing-editable Dashboard Header (hero banner above the
// Applications section on the Dashboard). Uses the same news_editor
// scope as Partner News so Marketing staff can update it without a
// super admin round-trip.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, ExternalLink, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = { image_url: "", link_url: "", alt_text: "", active: false };

const readFileB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

// Convert a `/api/news/image/{id}` reference into an absolute URL for
// preview / embedding in the dashboard. External URLs are returned as-is.
// NB: `api.defaults.baseURL` already ends with `/api`, so concatenating it
// with a `/api/...` value doubles the prefix (`/api/api/...` → 404).
// Strip the trailing `/api` so the preview URL resolves correctly.
const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${url}`;
};

export function DashboardHeaderEditor({ canManage }) {
  const [draft, setDraft] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/header");
      setDraft({ ...EMPTY, ...(data.header || {}) });
    } catch (e) {
      toast.error("Could not load Dashboard Header");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max image size is 8 MB"); return; }
    setUploading(true);
    try {
      const b64 = await readFileB64(file);
      const { data } = await api.post("/news/image", {
        data: b64, filename: file.name, content_type: file.type,
      });
      set({ image_url: data.url });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dashboard/header", {
        image_url: draft.image_url || "",
        link_url:  draft.link_url  || "",
        alt_text:  draft.alt_text  || "",
        active:    !!draft.active,
      });
      toast.success("Dashboard Header saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const clear = () => {
    set({ image_url: "", link_url: "", alt_text: "", active: false });
  };

  const preview = resolveImageUrl(draft.image_url);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
         data-testid="dashboard-header-editor">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-800">Dashboard Header (Hero Banner)</h4>
          <p className="text-[11px] text-slate-500">Shows above the Applications section on everyone&apos;s dashboard. Marketing-editable.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="dashheader-active" className="text-xs text-slate-600">Active</Label>
          <Switch id="dashheader-active"
                  data-testid="dashheader-active-switch"
                  checked={!!draft.active}
                  disabled={!canManage || loading}
                  onCheckedChange={(v) => set({ active: v })} />
        </div>
      </div>

      {loading ? (
        <div className="flex h-24 items-center justify-center text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Image URL</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="dashheader-image-url-input"
                  disabled={!canManage}
                  value={draft.image_url}
                  onChange={(e) => set({ image_url: e.target.value })}
                  placeholder="Paste an image URL or use Upload →" />
                <input ref={fileRef} type="file" accept="image/*"
                       className="hidden" data-testid="dashheader-file-input"
                       onChange={(e) => upload(e.target.files?.[0])} />
                <Button variant="outline" size="sm" disabled={!canManage || uploading}
                        onClick={() => fileRef.current?.click()}
                        data-testid="dashheader-upload-btn"
                        className="shrink-0">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Upload
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Click-through URL (opens in a new tab)</Label>
              <Input
                data-testid="dashheader-link-url-input"
                disabled={!canManage}
                value={draft.link_url}
                onChange={(e) => set({ link_url: e.target.value })}
                placeholder="https://unitedcloud.ca/articles/..." />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alt text (accessibility & SEO)</Label>
            <Input
              data-testid="dashheader-alt-input"
              disabled={!canManage}
              value={draft.alt_text}
              onChange={(e) => set({ alt_text: e.target.value })}
              placeholder="e.g. Meet UC Connect Premium Video — read the announcement" />
          </div>

          {preview && (
            <div className="space-y-1">
              <Label className="text-xs">Preview</Label>
              <a href={draft.link_url || "#"} target="_blank" rel="noreferrer"
                 data-testid="dashheader-preview-link"
                 className="block overflow-hidden rounded-lg border border-slate-200 shadow-sm hover:shadow transition">
                <img src={preview} alt={draft.alt_text || "Dashboard header preview"}
                     data-testid="dashheader-preview-image"
                     className="block h-auto w-full" />
              </a>
              {draft.link_url && (
                <p className="text-[11px] text-slate-500">Opens in a new tab: <a href={draft.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{draft.link_url}<ExternalLink className="h-3 w-3" /></a></p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button size="sm" onClick={save} disabled={!canManage || saving}
                    data-testid="dashheader-save-btn"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Dashboard Header
            </Button>
            <Button size="sm" variant="outline"
                    onClick={clear}
                    disabled={!canManage || saving}
                    data-testid="dashheader-clear-btn">
              <Trash2 className="h-4 w-4" /> Clear
            </Button>
            {!canManage && (
              <span className="text-[11px] text-slate-500">Only Super Admins and Marketing staff can edit this.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
